import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart3, RefreshCw, Users, CreditCard, Eye } from "lucide-react";

const OWNER_EMAIL = "jennifer.chandler@gmsdk12.org";

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E8E6DF] rounded-lg p-5">
      <div className="flex items-center gap-2 text-[#5D6D63] text-sm mb-1">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="font-heading text-3xl text-[#1C2321]">{value}</div>
    </div>
  );
}

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/attributions");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load referral analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (user && (user.email || "").toLowerCase() !== OWNER_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  const t = data?.totals || {};
  const rows = data?.rows || [];
  const unattr = data?.unattributed || { signups: 0, paid: 0 };

  return (
    <div data-testid="referrals-page" className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-[#1C2321] flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-[#971a8c]" /> Referral analytics
          </h1>
          <p className="text-sm text-[#5D6D63] mt-1">
            Which landing pages and sources drive visits, signups and paid conversions.
          </p>
        </div>
        <Button
          data-testid="referrals-refresh-btn"
          variant="outline"
          onClick={load}
          disabled={loading}
          className="h-10 rounded-md"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Eye} label="Total visits" value={t.visits ?? 0} testid="stat-visits" />
        <StatCard icon={Users} label="Attributed signups" value={t.attributed_signups ?? 0} testid="stat-signups" />
        <StatCard icon={CreditCard} label="Attributed paid" value={t.attributed_paid ?? 0} testid="stat-paid" />
        <StatCard icon={Users} label="Direct / unknown" value={unattr.signups ?? 0} testid="stat-unattributed" />
      </div>

      <div className="bg-white border border-[#E8E6DF] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="referrals-table" className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#5D6D63] bg-[#F9F8F6] border-b border-[#E8E6DF]">
                <th className="px-4 py-3 font-medium">Referrer / source</th>
                <th className="px-4 py-3 font-medium text-right">Visits</th>
                <th className="px-4 py-3 font-medium text-right">Signups</th>
                <th className="px-4 py-3 font-medium text-right">Visit→Signup</th>
                <th className="px-4 py-3 font-medium text-right">Paid</th>
                <th className="px-4 py-3 font-medium text-right">Signup→Paid</th>
                <th className="px-4 py-3 font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8B9891]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8B9891]" data-testid="referrals-empty">
                  No referral data yet. Traffic with a <code>?ref=</code> tag will show up here.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.ref} data-testid={`referral-row-${r.ref}`} className="border-b border-[#F0EEE8] hover:bg-[#FBFAF8]">
                  <td className="px-4 py-3 font-medium text-[#1C2321]">{r.ref}</td>
                  <td className="px-4 py-3 text-right text-[#3A4A42]">{r.visits}</td>
                  <td className="px-4 py-3 text-right text-[#3A4A42]">{r.signups}</td>
                  <td className="px-4 py-3 text-right text-[#5D6D63]">{pct(r.signup_rate)}</td>
                  <td className="px-4 py-3 text-right text-[#3A4A42]">{r.paid}</td>
                  <td className="px-4 py-3 text-right text-[#5D6D63]">{pct(r.paid_rate)}</td>
                  <td className="px-4 py-3 text-[#8B9891] text-xs">
                    {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
              {!loading && (unattr.signups > 0 || unattr.paid > 0) && (
                <tr data-testid="referral-row-unattributed" className="bg-[#F9F8F6]">
                  <td className="px-4 py-3 italic text-[#5D6D63]">Direct / unknown</td>
                  <td className="px-4 py-3 text-right text-[#8B9891]">—</td>
                  <td className="px-4 py-3 text-right text-[#3A4A42]">{unattr.signups}</td>
                  <td className="px-4 py-3 text-right text-[#8B9891]">—</td>
                  <td className="px-4 py-3 text-right text-[#3A4A42]">{unattr.paid}</td>
                  <td className="px-4 py-3 text-right text-[#8B9891]">—</td>
                  <td className="px-4 py-3 text-[#8B9891] text-xs">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#8B9891] mt-3">
        Signups are attributed on a first-touch basis from the sign-in link's <code>?ref=</code> tag.
        "Paid" counts users currently trialing or active.
      </p>
    </div>
  );
}
